import { CorporateDashboardContext } from "contexts/CorporateDashboardContext/CorporateDashboardContext";
import { useContext, useMemo } from "react";
import NoData from "components/NoData/NoData.view";
import { useStyles } from "./TopFiveFailureCodesAllMills.styles";
import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import { CircularProgress } from "@mui/material";
import ReportDowntimeTopFiveBarChart from "components/ReportDowntimeTopFiveBarChart/ReportDowntimeTopFiveBarChart";
import { IChartBar } from "components/ReportDowntimeTopFiveBarChart/ReportDowntimeTopFiveBarChartInterfaces";
import { DefaultTranslationFieldsInTable, DisplayInOptionsEnum } from "constants/constants";
import { ICorporateReportCardProps } from "../../CorporateReportsInterfaces";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { useTranslation } from "react-i18next";

const TopFiveFailureCodesAllMills = ({ countryCode }: ICorporateReportCardProps) => {
    const classes = useStyles();
    const { selectedFailureType, handleSetSelectedFailureCode, filters, totalDowntimeSeconds } =
        useContext(CorporateDashboardContext);
    const { downtimeSummaryByFailureCode, loading } =
        CorporationsEntity.GetDowntimeSummaryByFailureCodeCorporateDashboard(filters, selectedFailureType?.id);
    const { i18n } = useTranslation();

    //we're mapping the data as Nivo needs it for its graphs
    const graphData: IChartBar[] = useMemo(
        () =>
            downtimeSummaryByFailureCode
                .sort((a, b) =>
                    filters.displayIn === DisplayInOptionsEnum.MINUTES ||
                    filters.displayIn === DisplayInOptionsEnum.PERCENT
                        ? b.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.seconds -
                          a.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.seconds
                        : b.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.occurrences -
                          a.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.occurrences,
                )
                .slice(0, 5)
                .reverse()
                .map((failureCode) => {
                    const defaultFailureCodeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        failureCode,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                    );
                    const secondsToMinutes =
                        Math.round(
                            failureCode.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.seconds / 60,
                        ) < 1
                            ? 1
                            : Math.round(
                                  failureCode.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.seconds / 60,
                              );
                    return {
                        id: defaultFailureCodeTranslatedName,
                        countryCode: countryCode as string,
                        [`${defaultFailureCodeTranslatedName}Color`]: failureCode.failure_type.color_code,
                        [defaultFailureCodeTranslatedName]:
                            filters.displayIn === DisplayInOptionsEnum.MINUTES
                                ? secondsToMinutes
                                : filters.displayIn === DisplayInOptionsEnum.PERCENT
                                ? totalDowntimeSeconds
                                    ? (100 *
                                          failureCode.event_failure_code_summary_per_mcs_aggregate.aggregate.sum
                                              .seconds) /
                                      totalDowntimeSeconds
                                    : 0
                                : failureCode.event_failure_code_summary_per_mcs_aggregate.aggregate.sum.occurrences,
                    };
                }),
        [filters.displayIn, downtimeSummaryByFailureCode, totalDowntimeSeconds, countryCode, i18n.language],
    );

    return loading ? (
        <div className={classes.noData}>
            <CircularProgress size={24} color="info" />
        </div>
    ) : graphData.length > 0 ? (
        <ReportDowntimeTopFiveBarChart
            data={graphData}
            index="id"
            noPercent={filters.displayIn !== DisplayInOptionsEnum.PERCENT}
            onBarClick={(value) => {
                const failureCodeSelected = downtimeSummaryByFailureCode.find((failureCode) => {
                    const defaultFailureCodeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        failureCode,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                    );
                    return defaultFailureCodeTranslatedName === value;
                });
                if (failureCodeSelected)
                    handleSetSelectedFailureCode({
                        id: failureCodeSelected.failure_code_id,
                        name: failureCodeSelected.name,
                        default_failure_code_translations: failureCodeSelected.default_failure_code_translations,
                    });
            }}
        />
    ) : (
        <NoData className={classes.noData} />
    );
};

export default TopFiveFailureCodesAllMills;
